// admin-ui/src/components/AnchorStatus.tsx
// ALL RIGHTS RESERVED

/**
 * Anchor Chain Status — Wide Widget
 * On-chain verification display per Contract Section 1.4.3
 *
 * Rendered by RoleDashboard inside a `widget-card size-full` wrapper.
 * Styles live in components/styles/widgets.css (ANCHOR CHAIN section).
 */

import { AnchorChainStatus } from '../api/client';
import './styles/widgets.css';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface AnchorStatusProps {
  anchors: AnchorChainStatus;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function formatTimestamp(ts: string | null): string {
  if (!ts) return 'Never';
  return new Date(ts).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-8)}`;
}

function getTimeSince(ts: string | null): string {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h ago`;
  if (hours > 0) return `${hours}h ago`;
  const minutes = Math.floor(diff / (1000 * 60));
  return `${minutes}m ago`;
}

// ═══════════════════════════════════════════════════════
// FALLBACK DATA (mock for isMock: true)
// ═══════════════════════════════════════════════════════

const EMPTY_ANCHORS: AnchorChainStatus = {
  chainLength: 0,
  lastAnchor: null,
  genesisTimestamp: null,
  verified: false,
  errors: ['No anchors found - genesis required'],
};

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function AnchorStatus({ anchors }: AnchorStatusProps) {
  const { chainLength, lastAnchor, genesisTimestamp, verified, errors } =
    anchors ?? EMPTY_ANCHORS;

  return (
    <div className="anchor-chain">
      {/* ── Summary metrics row ────────────────────────── */}
      <div className="anchor-chain-summary">
        <div className="anchor-chain-metric">
          <span className="anchor-chain-label">Chain Length</span>
          <span className="anchor-chain-value">{chainLength}</span>
          <span className="anchor-chain-unit">anchors</span>
        </div>

        <div className="anchor-chain-metric">
          <span className="anchor-chain-label">Chain Status</span>
          <span
            className={`anchor-chain-status ${verified ? 'is-verified' : 'is-error'}`}
          >
            {verified ? '✓ Verified' : '⚠ Issues Detected'}
          </span>
        </div>

        <div className="anchor-chain-metric">
          <span className="anchor-chain-label">Genesis</span>
          <span className="anchor-chain-value">
            {formatTimestamp(genesisTimestamp)}
          </span>
        </div>
      </div>

      {/* ── Latest anchor details ─────────────────────── */}
      {lastAnchor && (
        <div className="anchor-chain-latest">
          <h4 className="anchor-chain-heading">Latest Anchor</h4>
          <div className="anchor-chain-details">
            <div className="anchor-chain-detail">
              <span className="anchor-chain-detail-label">Sequence</span>
              <span className="anchor-chain-detail-value">
                #{lastAnchor.sequence}
              </span>
            </div>
            <div className="anchor-chain-detail">
              <span className="anchor-chain-detail-label">Timestamp</span>
              <span className="anchor-chain-detail-value">
                {formatTimestamp(lastAnchor.timestamp)}
                <small className="anchor-chain-ago">
                  {getTimeSince(lastAnchor.timestamp)}
                </small>
              </span>
            </div>
            <div className="anchor-chain-detail">
              <span className="anchor-chain-detail-label">Transactions</span>
              <span className="anchor-chain-detail-value">
                {lastAnchor.txCount}
              </span>
            </div>
            <div className="anchor-chain-detail anchor-chain-detail--wide">
              <span className="anchor-chain-detail-label">Hash</span>
              <code
                className="anchor-chain-hash"
                title={lastAnchor.hash}
              >
                {truncateHash(lastAnchor.hash)}
              </code>
            </div>
          </div>
        </div>
      )}

      {/* ── Empty state ───────────────────────────────── */}
      {!lastAnchor && (
        <div className="anchor-chain-empty">
          <p className="anchor-chain-empty-msg">
            ⚠️ No anchors found. Genesis anchor required.
          </p>
          <code className="anchor-chain-cmd">
            cxctl anchor genesis --reserve 400000000 --deposits ~/cx/cxxr/tools/cxctl/data/default-genesis-deposits.json
          </code>
        </div>
      )}

      {/* ── Chain errors ──────────────────────────────── */}
      {errors.length > 0 && (
        <div className="anchor-chain-errors">
          <h4 className="anchor-chain-heading">Chain Errors</h4>
          <ul className="anchor-chain-error-list">
            {errors.map((error, i) => (
              <li key={i} className="anchor-chain-error-item">
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Actions ───────────────────────────────────── */}
      <div className="anchor-chain-actions">
        <button className="btn-secondary btn-sm" disabled>
          Verify Chain
        </button>
        <button className="btn-secondary btn-sm" disabled>
          Export Anchors
        </button>
      </div>
    </div>
  );
}

export default AnchorStatus;
